export const FIRST_CLEAR_PER_STAR = 10;
export const BOSS_MULTIPLIER = 2;
export const MAX_STARS = 3;

export const STORY_LEVELS_PER_WORLD: Readonly<Record<number, number>> = {
  1: 10,
  2: 10,
  3: 8,
  4: 8,
  5: 8,
  6: 8,
  7: 8,
};

export const RESOURCE_NAMES: Readonly<Record<number, string>> = {
  1: 'Supplies',
  2: 'Rigging',
  3: 'Copper Wire',
  4: 'Aether',
  5: 'Salvage',
  6: 'Forged Papers',
  7: 'Intel',
};

export const AREA_ID_FOR_WORLD = {
  1: 'smugglersCellar',
  2: 'airshipDock',
  3: 'generatorRoom',
  4: 'aetherTanks',
  5: 'workshop',
  6: 'forgeryOffice',
  7: 'warRoom',
} as const satisfies Readonly<Record<number, string>>;

export const AREA_NAMES: Readonly<Record<string, string>> = {
  smugglersCellar: "Smuggler's Cellar",
  airshipDock: 'Airship Dock',
  generatorRoom: 'Generator Room',
  aetherTanks: 'Aether Tanks',
  workshop: 'Workshop',
  forgeryOffice: 'Forgery Office',
  warRoom: 'War Room',
};

export const AREA_STAGE_COSTS: readonly number[] = [60, 120, 240];
export const MAX_AREA_STAGE = 3;

export const PERK_STAGE = 2;
export const RUN_REWARD_PERK_BONUS_PERCENT = 20;
export const REROLLS_PER_DAY = 1;
export const SUPPLY_RUN_SLOTS = 3;
export const SUPPLY_RUN_SLOTS_WITH_CELLAR_PERK = 4;

export const RUN_REWARD_THREE_STARS = 15;
export const RUN_REWARD_BEAT_PAR = 20;
export const RUN_REWARD_NO_HINTS = 20;
export const RUN_REWARD_ALT_OPTIMUM = 25;

const PERK_AREA_SUPPLY_SLOTS = AREA_ID_FOR_WORLD[1];
const PERK_AREA_REROLL = AREA_ID_FOR_WORLD[2];
const PERK_AREA_RUN_BONUS = AREA_ID_FOR_WORLD[3];
const PERK_AREA_RADIO_HINT = AREA_ID_FOR_WORLD[6];

const CAMPAIGN_LEVEL_ID = /^w(\d+)_l(\d+)$/;

interface CampaignLevelId {
  readonly worldId: number;
  readonly levelIndex: number;
}

function parseCampaignLevelId(id: string): CampaignLevelId | null {
  const [, world, level] = CAMPAIGN_LEVEL_ID.exec(id) ?? [];
  if (world === undefined || level === undefined) return null;

  const worldId = Number(world);
  if (STORY_LEVELS_PER_WORLD[worldId] === undefined) return null;

  return { worldId, levelIndex: Number(level) };
}

export function worldIdForLevel(id: string): number | null {
  return parseCampaignLevelId(id)?.worldId ?? null;
}

export function isCampaignLevel(id: string): boolean {
  return parseCampaignLevelId(id) !== null;
}

export function storyLevelCount(worldId: number): number {
  return STORY_LEVELS_PER_WORLD[worldId] ?? 0;
}

export function isBossLevel(id: string): boolean {
  const parsed = parseCampaignLevelId(id);
  return parsed !== null && storyLevelCount(parsed.worldId) === parsed.levelIndex;
}

export function resourceNameFor(worldId: number): string {
  return RESOURCE_NAMES[worldId] ?? '';
}

export function areaIdForWorld(worldId: number): string | null {
  return AREA_ID_FOR_WORLD[worldId as keyof typeof AREA_ID_FOR_WORLD] ?? null;
}

export function worldIdForArea(areaId: string): number | null {
  for (const [worldId, id] of Object.entries(AREA_ID_FOR_WORLD)) {
    if (id === areaId) return Number(worldId);
  }
  return null;
}

export function areaName(areaId: string): string {
  return AREA_NAMES[areaId] ?? areaId;
}

export function upgradeCost(currentStage: number): number | null {
  if (currentStage < 0 || currentStage >= MAX_AREA_STAGE) return null;
  return AREA_STAGE_COSTS[currentStage] ?? null;
}

function clampStars(stars: number): number {
  return Math.min(MAX_STARS, Math.max(0, stars));
}

export interface StarRewardInput {
  readonly levelId: string;
  readonly newStars: number;
  readonly alreadyRewardedStars: number;
}

export function starReward({ levelId, newStars, alreadyRewardedStars }: StarRewardInput): number {
  if (worldIdForLevel(levelId) === null) return 0;

  const multiplier = isBossLevel(levelId) ? BOSS_MULTIPLIER : 1;
  const paid = clampStars(alreadyRewardedStars) * FIRST_CLEAR_PER_STAR * multiplier;
  const due = clampStars(newStars) * FIRST_CLEAR_PER_STAR * multiplier;

  return Math.max(0, due - paid);
}

export interface RebellionState {
  readonly resources: Readonly<Record<number, number>>;
  readonly areaLevels: Readonly<Record<string, number>>;
  readonly bestRewardedStars: Readonly<Record<string, number>>;
  readonly supplyRunDate: string | null;
  readonly completedRunIds: readonly string[];
  readonly rerollsUsedToday: number;
  readonly lastRadioHintDate: string | null;
}

export const EMPTY_REBELLION_STATE: RebellionState = {
  resources: {},
  areaLevels: {},
  bestRewardedStars: {},
  supplyRunDate: null,
  completedRunIds: [],
  rerollsUsedToday: 0,
  lastRadioHintDate: null,
};

export interface RewardReceipt {
  readonly worldId: number;
  readonly resourceName: string;
  readonly amount: number;
}

export interface RewardOutcome {
  readonly state: RebellionState;
  readonly receipt: RewardReceipt;
}

export interface UpgradeOutcome {
  readonly state: RebellionState;
  readonly stage: number;
}

export function isEmptyReceipt(receipt: RewardReceipt): boolean {
  return receipt.amount <= 0;
}

function receiptFor(worldId: number, amount: number): RewardReceipt {
  return { worldId, resourceName: resourceNameFor(worldId), amount };
}

export function resourceFor(state: RebellionState, worldId: number): number {
  return state.resources[worldId] ?? 0;
}

export function areaStage(state: RebellionState, areaId: string): number {
  return state.areaLevels[areaId] ?? 0;
}

export function hasPerk(state: RebellionState, areaId: string): boolean {
  return areaStage(state, areaId) >= PERK_STAGE;
}

function credit(state: RebellionState, worldId: number, amount: number): RebellionState {
  return {
    ...state,
    resources: { ...state.resources, [worldId]: resourceFor(state, worldId) + amount },
  };
}

export function rewardLevel(state: RebellionState, levelId: string, stars: number): RewardOutcome {
  const worldId = worldIdForLevel(levelId);
  if (worldId === null) {
    return { state, receipt: { worldId: 0, resourceName: '', amount: 0 } };
  }

  const alreadyRewarded = state.bestRewardedStars[levelId] ?? 0;
  const amount = starReward({ levelId, newStars: stars, alreadyRewardedStars: alreadyRewarded });
  if (amount <= 0) return { state, receipt: receiptFor(worldId, 0) };

  const credited = credit(state, worldId, amount);
  return {
    state: {
      ...credited,
      bestRewardedStars: {
        ...state.bestRewardedStars,
        [levelId]: Math.max(stars, alreadyRewarded),
      },
    },
    receipt: receiptFor(worldId, amount),
  };
}

export function applyRunRewardPerk(state: RebellionState, reward: number): number {
  if (!hasPerk(state, PERK_AREA_RUN_BONUS)) return reward;
  return Math.ceil((reward * (100 + RUN_REWARD_PERK_BONUS_PERCENT)) / 100);
}

export function supplyRunSlots(state: RebellionState): number {
  return hasPerk(state, PERK_AREA_SUPPLY_SLOTS)
    ? SUPPLY_RUN_SLOTS_WITH_CELLAR_PERK
    : SUPPLY_RUN_SLOTS;
}

export function supplyRunSalt(state: RebellionState, today: string): number {
  return state.supplyRunDate === today ? state.rerollsUsedToday : 0;
}

export function completedRunIdsOn(state: RebellionState, today: string): readonly string[] {
  return state.supplyRunDate === today ? state.completedRunIds : [];
}

function startDay(state: RebellionState, today: string): RebellionState {
  if (state.supplyRunDate === today) return state;
  return { ...state, supplyRunDate: today, completedRunIds: [], rerollsUsedToday: 0 };
}

export interface RunPayout {
  readonly id: string;
  readonly worldId: number;
  readonly reward: number;
}

export function completeRun(state: RebellionState, run: RunPayout, today: string): RewardOutcome {
  const today_ = startDay(state, today);
  if (today_.completedRunIds.includes(run.id)) {
    return { state, receipt: receiptFor(run.worldId, 0) };
  }

  const amount = applyRunRewardPerk(today_, run.reward);
  const credited = credit(today_, run.worldId, amount);

  return {
    state: { ...credited, completedRunIds: [...today_.completedRunIds, run.id] },
    receipt: receiptFor(run.worldId, amount),
  };
}

export function canUpgradeArea(state: RebellionState, areaId: string): boolean {
  const worldId = worldIdForArea(areaId);
  if (worldId === null) return false;

  const cost = upgradeCost(areaStage(state, areaId));
  return cost !== null && resourceFor(state, worldId) >= cost;
}

export function upgradeArea(state: RebellionState, areaId: string): UpgradeOutcome | null {
  const worldId = worldIdForArea(areaId);
  if (worldId === null) return null;

  const currentStage = areaStage(state, areaId);
  const cost = upgradeCost(currentStage);
  if (cost === null || resourceFor(state, worldId) < cost) return null;

  const stage = currentStage + 1;
  return {
    state: {
      ...state,
      resources: { ...state.resources, [worldId]: resourceFor(state, worldId) - cost },
      areaLevels: { ...state.areaLevels, [areaId]: stage },
    },
    stage,
  };
}

export function canReroll(state: RebellionState, today: string): boolean {
  if (!hasPerk(state, PERK_AREA_REROLL)) return false;
  return supplyRunSalt(state, today) < REROLLS_PER_DAY;
}

export function useReroll(state: RebellionState, today: string): RebellionState | null {
  if (!canReroll(state, today)) return null;

  const started = startDay(state, today);
  return { ...started, rerollsUsedToday: started.rerollsUsedToday + 1 };
}

export function radioHintAvailable(state: RebellionState, today: string): boolean {
  if (!hasPerk(state, PERK_AREA_RADIO_HINT)) return false;
  return state.lastRadioHintDate !== today;
}

export function markRadioHintUsed(state: RebellionState, today: string): RebellionState {
  return { ...state, lastRadioHintDate: today };
}
